import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  CHART_COLORS,
  CHART_FONT_CONFIGS,
  CHART_LEGEND_CONFIG,
  CHART_TOOLTIP_CONFIG,
  INITIATIVE_STATUS_COLORS,
} from '@/src/lib/chartConfig.js';
import { WidgetWrapper } from '@/src/components/projects/ProjectDashboard/WidgetWrapper.jsx';
import { WidgetInner } from '@/src/components/projects/ProjectDashboard/WidgetInner.jsx';
import { getOrgTermsContent } from '@/src/content/utils.js';
import { initiativeStatusesWidgetContent } from '@/src/components/EmptyStates.jsx';
import Empty from '@/src/components/Empty.jsx';
import { INITIATIVE_TYPES } from '@/src/lib/schema.js';
import { buildPath } from '@/src/components/Link.jsx';
import { PROJECTS_INITIATIVES } from '@/src/constants/routes.js';

export const InitiativeStatusesWidget = ({ data }) => {
  const navigate = useNavigate();
  const { slug, projectId } = useParams();

  const handleNavigateToInitiatives = () => {
    const path = buildPath(PROJECTS_INITIATIVES, { slug, projectId });
    navigate(path);
  };

  const hasData = INITIATIVE_TYPES.some(
    (type) =>
      data[type].completed > 0 ||
      data[type].inProgress > 0 ||
      data[type].notStarted > 0
  );

  const dataSet = {
    labels: INITIATIVE_TYPES.map(
      (type) => type.charAt(0).toUpperCase() + type.slice(1)
    ),
    datasets: [
      {
        label: 'Completed',
        data: INITIATIVE_TYPES.map((type) => data[type].completed),
        backgroundColor: INITIATIVE_STATUS_COLORS.completed,
        borderRadius: 0,
        barThickness: 24,
      },
      {
        label: 'In Progress',
        data: INITIATIVE_TYPES.map((type) => data[type].inProgress),
        backgroundColor: INITIATIVE_STATUS_COLORS.inProgress,
        borderRadius: 0,
        barThickness: 24,
      },
      {
        label: 'Not Started',
        data: INITIATIVE_TYPES.map((type) => data[type].notStarted),
        backgroundColor: INITIATIVE_STATUS_COLORS.notStarted,
        borderRadius: 0,
        barThickness: 24,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
          tickLength: 5,
          color: CHART_COLORS.axisBorder,
        },
        border: {
          display: true,
          color: CHART_COLORS.axisBorder,
          width: CHART_COLORS.axisBorderWidth,
        },
        ticks: {
          font: CHART_FONT_CONFIGS.regular,
          color: CHART_COLORS.textMuted,
          stepSize: 1,
          precision: 0,
        },
      },
      y: {
        stacked: true,
        grid: {
          drawOnChartArea: false,
          drawTicks: true,
          tickLength: 5,
          color: CHART_COLORS.axisBorder,
        },
        border: {
          display: true,
          color: CHART_COLORS.axisBorder,
          width: CHART_COLORS.axisBorderWidth,
        },
        ticks: {
          font: CHART_FONT_CONFIGS.regular,
          color: CHART_COLORS.textMuted,
        },
      },
    },
    plugins: {
      legend: CHART_LEGEND_CONFIG,
      tooltip: {
        ...CHART_TOOLTIP_CONFIG,
        callbacks: {
          ...CHART_TOOLTIP_CONFIG.callbacks,
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.x}`;
          },
        },
      },
    },
  };

  return (
    <WidgetWrapper className="initiatives-statuses-widget">
      <h2 className="m-b-18">
        Change {getOrgTermsContent('initiatives')} Statuses by Category
      </h2>
      {hasData ? (
        <WidgetInner height={400}>
          <div className="p-y-24">
            <div style={{ height: '300px', width: '100%' }}>
              <Bar data={dataSet} options={options} />
            </div>
          </div>
        </WidgetInner>
      ) : (
        <WidgetInner height={400} className="flex flex-col">
          <Empty
            simplified
            content={initiativeStatusesWidgetContent}
            action={handleNavigateToInitiatives}
          />
        </WidgetInner>
      )}
    </WidgetWrapper>
  );
};
